/**
 * Plat menu → Stock-Nachfrage (legacy includedStockItems + Recipe engine Phase 2).
 *
 * Active recipe for a dish wins (XOR). Falls back to includedStockItems.
 * Output stock lines stay compatible with planSaleLotDeductions / FIFO.
 * Does not touch lotSystem or allocation.
 */

import { sameProductId } from '../../utils/productIdUtils';
import {
  isRestaurantDishProduct,
  isRestaurantIngredientProduct
} from '../../utils/restaurantMode';
import { resolveActiveRecipe } from './recipe/resolveActiveRecipe';
import { explodeRecipeToStockDemand } from './recipe/recipeExplosion';
import { resolveIngredientDemandLocation } from './restaurantStockLocations';

/**
 * Prefer first non-empty recipes list (saleData `[]` must not mask shell/payload).
 * @param {...unknown} candidates
 * @returns {unknown[]}
 */
export function resolveRecipesForPlanning(...candidates) {
  for (const c of candidates) {
    if (Array.isArray(c) && c.length > 0) return c;
  }
  return [];
}

/**
 * @param {unknown} raw
 * @returns {{ productId: string, quantity: number }[]}
 */
export function normalizeIncludedStockItems(raw) {
  if (!Array.isArray(raw)) return [];
  const byId = new Map();
  for (const entry of raw) {
    const productId = String(entry?.productId ?? entry?.id ?? '').trim();
    const quantity = Number(entry?.quantity);
    if (!productId || !Number.isFinite(quantity) || quantity <= 0) continue;
    const prev = byId.get(productId) || 0;
    byId.set(productId, prev + quantity);
  }
  return [...byId.entries()].map(([productId, quantity]) => ({ productId, quantity }));
}

/**
 * Keep only links that point to real stock ingredients in the catalog.
 * @param {unknown} raw
 * @param {object[]} products
 */
export function sanitizeIncludedStockItems(raw, products = []) {
  const list = normalizeIncludedStockItems(raw);
  if (list.length === 0) return [];
  const catalog = Array.isArray(products) ? products : [];
  return list.filter((link) => {
    const product = catalog.find((p) =>
      sameProductId(p?.id ?? p?.productId, link.productId)
    );
    if (!product) return false;
    if (isRestaurantDishProduct(product)) return false;
    if (product.trackInventory === false) return false;
    return isRestaurantIngredientProduct(product) || !product.productRole;
  });
}

/**
 * Resolve links from a cart/sale line or catalog product.
 */
export function resolveIncludedStockItemsForLine(line, catalogProduct = null) {
  const fromLine = normalizeIncludedStockItems(line?.includedStockItems);
  if (fromLine.length > 0) return fromLine;
  return normalizeIncludedStockItems(catalogProduct?.includedStockItems);
}

/**
 * Build productsById map/function for unit conversion during recipe explode.
 * @param {object[]|Function|Map|Record|null} productsById
 * @param {object[]} [catalogProducts]
 * @param {Function|null} [findProduct]
 */
function resolveProductsLookup(productsById, catalogProducts, findProduct) {
  if (typeof productsById === 'function') return productsById;
  if (productsById instanceof Map) return productsById;
  if (productsById && typeof productsById === 'object' && !Array.isArray(productsById)) {
    return productsById;
  }
  if (typeof findProduct === 'function') return findProduct;
  const catalog = Array.isArray(catalogProducts)
    ? catalogProducts
    : Array.isArray(productsById)
      ? productsById
      : [];
  if (catalog.length === 0) return null;
  return (productId) =>
    catalog.find((p) => sameProductId(p?.id ?? p?.productId, productId)) || null;
}

/**
 * Absolute inventory demand for one sold dish line (already × portionsSold).
 * Active recipe XOR includedStockItems (never both).
 *
 * @param {object|null} line
 * @param {object|null} catalogProduct
 * @param {number} portionsSold
 * @param {{
 *   recipes?: unknown[],
 *   productsById?: object[]|Function|Map|Record,
 *   catalogProducts?: object[],
 *   findProduct?: Function
 * }} [options]
 * @returns {Array<{
 *   productId: string,
 *   quantity: number,
 *   recipeId?: string,
 *   recipeVersion?: number,
 *   source: 'recipe'|'includedStockItems'
 * }>}
 */
export function resolveDishIngredientDemand(
  line,
  catalogProduct = null,
  portionsSold = 1,
  options = {}
) {
  const sold = Number(portionsSold);
  if (!Number.isFinite(sold) || sold <= 0) return [];

  const dishId =
    line?.productId ?? line?.id ?? catalogProduct?.id ?? catalogProduct?.productId;
  const recipes = options.recipes;
  const productsLookup = resolveProductsLookup(
    options.productsById,
    options.catalogProducts,
    options.findProduct
  );

  if (Array.isArray(recipes) && recipes.length > 0 && dishId != null && dishId !== '') {
    // Prefer checkout snapshot (sale/cart line) so void/report match consumed stock.
    let recipe = null;
    const snapId = line?.recipeId != null && line.recipeId !== '' ? String(line.recipeId) : '';
    if (snapId) {
      recipe = recipes.find((r) => r && String(r.id) === snapId) || null;
    }
    if (!recipe) {
      recipe = resolveActiveRecipe(recipes, dishId);
    }
    if (recipe) {
      // POS SSOT: soldQty ≡ explodeQuantity (recipe.portions = BOM base). See recipeUsageScale.
      const exploded = explodeRecipeToStockDemand({
        recipe,
        explodeQuantity: sold,
        portionsSold: sold,
        recipes,
        productsById: productsLookup,
        mergeByProduct: true
      });
      if (exploded.success && Array.isArray(exploded.demand)) {
        return exploded.demand
          .map((row) => ({
            productId: String(row.productId),
            quantity: Number(row.quantity) || 0,
            recipeId: row.recipeId,
            recipeVersion: row.recipeVersion,
            source: 'recipe'
          }))
          .filter((row) => row.productId && row.quantity > 0);
      }
      // Active/snapshot recipe exists but explode failed — fail closed (XOR).
      // Do not mix shadow includedStockItems (would diverge from FIFO that used recipe path).
      return [];
    }
  }

  const perUnit = resolveIncludedStockItemsForLine(line, catalogProduct);
  return perUnit.map((link) => ({
    productId: String(link.productId),
    quantity: Number(link.quantity) * sold,
    source: 'includedStockItems'
  })).filter((row) => row.productId && row.quantity > 0);
}

/**
 * Expand dish lines into stock demand scopes for cart validation.
 * Direct stock lines + dish recipe/included links (absolute qty).
 *
 * @param {object[]} cartOrProducts
 * @param {object[]} [catalogProducts]
 * @param {{ recipes?: unknown[] }} [options]
 * @returns {Map<string, number>} key = `${productId}|${location}` → requested qty
 */
export function buildStockDemandFromCart(
  cartOrProducts,
  catalogProducts = [],
  options = {}
) {
  const demand = new Map();
  const catalog = Array.isArray(catalogProducts) ? catalogProducts : [];
  const bump = (productId, location, qty) => {
    if (!productId || !(qty > 0)) return;
    const key = `${productId}|${location || 'Principal'}`;
    demand.set(key, (demand.get(key) || 0) + qty);
  };

  for (const item of Array.isArray(cartOrProducts) ? cartOrProducts : []) {
    if (!item) continue;
    const location = item.stockLocation || item.location || 'Principal';
    const lineQty = Number(item.quantity) || 0;
    if (!(lineQty > 0)) continue;

    // Same dish rule as expandSaleLinesWithDishIncludes: cart flag OR catalog product.
    const catalogProduct =
      catalog.find((p) =>
        sameProductId(p?.id ?? p?.productId, item.productId ?? item.id)
      ) || null;
    const isDish =
      item.isRestaurantDish === true ||
      isRestaurantDishProduct(item) ||
      isRestaurantDishProduct(catalogProduct);

    if (isDish) {
      const links = resolveDishIngredientDemand(item, catalogProduct, lineQty, {
        recipes: options.recipes,
        catalogProducts: catalog,
        productsById: options.productsById
      });
      // lineQty = POS sold units ≡ explodeQuantity via recipeUsageScale scaleForPosSale
      for (const link of links) {
        const ingredient =
          catalog.find((p) => sameProductId(p?.id ?? p?.productId, link.productId)) ||
          (typeof options.productsById === 'function'
            ? options.productsById(link.productId)
            : null);
        // Processing a dish BOM ⇒ restaurant stock triad rules for ingredients.
        const demandLoc = resolveIngredientDemandLocation(ingredient, location, {
          restaurantContext: true,
          businessType: options.businessType
        });
        bump(link.productId, demandLoc, link.quantity);
      }
      continue;
    }

    bump(String(item.productId), location, lineQty);
  }

  return demand;
}

/**
 * Expand sale/cart product lines for lot planning: keep dish rows, append synthetic stock rows.
 * @param {object[]} products
 * @param {(productId: *) => object|null} findProduct
 * @param {string} saleLocation
 * @param {{ recipes?: unknown[], productsById?: * }} [options]
 */
export function expandSaleLinesWithDishIncludes(
  products,
  findProduct,
  saleLocation = 'Principal',
  options = {}
) {
  const expanded = [];
  for (const productSale of Array.isArray(products) ? products : []) {
    if (!productSale) continue;
    const productId = productSale.productId;
    const quantity = Number(productSale.quantity);
    const product = typeof findProduct === 'function' ? findProduct(productId) : null;
    const isDish =
      productSale.isRestaurantDish === true || isRestaurantDishProduct(product);

    if (isDish) {
      expanded.push({
        ...productSale,
        isRestaurantDish: true
      });
      const portionsSold = Number.isFinite(quantity) ? quantity : 0;
      const links = resolveDishIngredientDemand(productSale, product, portionsSold, {
        recipes: options.recipes,
        productsById: options.productsById,
        findProduct,
        catalogProducts: options.catalogProducts
      });
      for (const link of links) {
        const ingredientProduct =
          (typeof findProduct === 'function' ? findProduct(link.productId) : null) ||
          (typeof options.productsById === 'function'
            ? options.productsById(link.productId)
            : null);
        const loc = resolveIngredientDemandLocation(
          ingredientProduct,
          productSale.stockLocation || saleLocation,
          { restaurantContext: true, businessType: options.businessType }
        );
        expanded.push({
          productId: link.productId,
          // already absolute (× portionsSold) from resolveDishIngredientDemand
          quantity: link.quantity,
          stockLocation: loc,
          fromDishProductId: productId,
          fromRecipeId: link.recipeId,
          fromRecipeVersion: link.recipeVersion,
          demandSource: link.source,
          isRestaurantDish: false
        });
      }
      continue;
    }

    expanded.push(productSale);
  }
  return expanded;
}

/**
 * Phase A guard: every dish line must resolve to stock demand (recipe and/or includes).
 * Direct stock/sellable drinks are not checked here.
 *
 * @param {object[]} products — sale/cart lines
 * @param {object[]} [catalogProducts]
 * @param {{ recipes?: unknown[] }} [options]
 * @returns {Array<{ productId: string, message: string, code: string }>}
 */
export function collectDishStockDemandErrors(products, catalogProducts = [], options = {}) {
  const catalog = Array.isArray(catalogProducts) ? catalogProducts : [];
  const recipes = resolveRecipesForPlanning(options.recipes);
  const errors = [];

  for (const line of Array.isArray(products) ? products : []) {
    if (!line) continue;
    const lineQty = Number(line.quantity) || 0;
    if (!(lineQty > 0)) continue;

    const catalogProduct =
      catalog.find((p) =>
        sameProductId(p?.id ?? p?.productId, line.productId ?? line.id)
      ) || null;
    const isDish =
      line.isRestaurantDish === true ||
      isRestaurantDishProduct(line) ||
      isRestaurantDishProduct(catalogProduct);
    if (!isDish) continue;

    const productId = String(line.productId ?? line.id ?? catalogProduct?.id ?? '');
    const demand = resolveDishIngredientDemand(line, catalogProduct, lineQty, {
      recipes,
      catalogProducts: catalog,
      productsById: options.productsById
    });

    if (demand.length > 0) continue;

    const hasActive = Boolean(resolveActiveRecipe(recipes, productId));
    const hasLineIncludes = resolveIncludedStockItemsForLine(line, catalogProduct).length > 0;

    let code = 'dish_no_stock_demand';
    let message =
      'Plat sans recette active ni composition stock — activez une recette (ou liens stock) avant la vente';
    if (hasActive) {
      code = 'dish_recipe_explode_failed';
      message =
        'Recette active invalide (explosion échouée) — corrigez la recette avant la vente';
    } else if (hasLineIncludes) {
      // includes present but demand empty after scale — shouldn't happen often
      code = 'dish_includes_empty';
      message = 'Composition stock du plat vide ou invalide';
    }

    errors.push({
      productId,
      productName: line.name || line.productName || catalogProduct?.name || productId,
      code,
      message
    });
  }

  return errors;
}
